import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import SearchIcon from "@material-ui/icons/Search";
import AiAssistantApi from "../../services/resources/AiAssistantApi";

export default function AiAssistantHistory({
  onSelect,
  selectedThreadId,
  disabled,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // refs for read-only values in callbacks
  const searchRef = useRef(search);
  searchRef.current = search;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const panelRef = useRef(null);
  const searchTimerRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await AiAssistantApi.getConversationHistory(
        searchRef.current || "",
      );
      setItems(data || []);
    } catch (err) {
      setError("Failed to load conversation history.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);

    // Clear search when opening
    if (!isOpenRef.current) {
      setSearch("");
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen, fetchHistory]);

  useEffect(() => {
    if (!isOpenRef.current) {
      return;
    }

    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchHistory();
    }, 300);

    return () => clearTimeout(searchTimerRef.current);
  }, [search, fetchHistory]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (item) => {
    onSelect(item);
    setIsOpen(false);
  };

  return (
    <div className="ai-assistant-modal__history-wrapper" ref={panelRef}>
      <button
        type="button"
        className="btn custom-btn btn-outline"
        onClick={toggleOpen}
        title="Conversation history"
        disabled={disabled}
      >
        History
      </button>
      {isOpen && (
        <div className="ai-assistant-modal__history-dropdown">
          <div className="ai-assistant-modal__history-search">
            <SearchIcon fontSize="default" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="ai-assistant-modal__history-list">
            {isLoading ? (
              <div className="ai-assistant-modal__history-empty">
                Loading...
              </div>
            ) : error ? (
              <div className="ai-assistant-modal__history-empty alert-danger">
                {error}
              </div>
            ) : items.length === 0 ? (
              <div className="ai-assistant-modal__history-empty">
                No conversations found.
              </div>
            ) : (
              items.map((item) => (
                <button
                  type="button"
                  key={item.threadID}
                  className={`ai-assistant-modal__history-item${
                    selectedThreadId === item.threadID ? " active" : ""
                  }`}
                  onClick={() => handleSelect(item)}
                >
                  <div className="ai-assistant-modal__history-item-header">
                    <span className="ai-assistant-modal__history-item-question">
                      {item.initialQuestion}
                    </span>
                    <span className="ai-assistant-modal__history-item-time">
                      {moment(item.createdOn).fromNow()}
                    </span>
                  </div>
                  <span className="ai-assistant-modal__history-item-meta">
                    {item.askedBy}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
